<template>
  <Modal
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)"
    :size="effectiveSize"
    :title="title"
    flush
    @close="$emit('close')"
    v-bind="$attrs"
  >
    <template #header="{ titleId }">
      <div class="info-head">
        <span class="info-head-icon" aria-hidden="true">
          <i :class="icon"></i>
        </span>
        <div class="info-head-titles">
          <span class="info-head-kicker">{{ kicker }}</span>
          <h2 class="info-head-title" :id="titleId">{{ title }}</h2>
        </div>
      </div>
    </template>

    <div class="info-layout" :class="{ 'has-toc': showToc }">
      <!-- Sommaire : construit depuis les sections du contenu, pas déclaré à la
           main — une aide qui gagne une section gagne son entrée toute seule. -->
      <div v-if="showToc" class="info-toc">
        <TocRail
          :sections="sections"
          :active-id="activeId"
          aria-label="Sommaire de l'aide"
          mobile="hidden"
          @select="goTo"
        />
      </div>

      <div ref="scrollerRef" class="info-scroller" @scroll.passive="syncActive">
        <div ref="contentRef" class="info-slot">
          <slot></slot>
        </div>
        <!-- Talon : sans lui, la dernière section ne peut pas remonter en haut
             du volet et le sommaire ne l'atteint jamais. -->
        <div v-if="showToc" class="info-tail" aria-hidden="true"></div>
      </div>
    </div>

    <template #footer>
      <slot name="footer">
        <Button variant="primary" @click="$emit('close')">
          {{ closeButtonText }}
        </Button>
      </slot>
    </template>
  </Modal>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import Modal from './Modal.vue'
import Button from './Button.vue'
import TocRail from './TocRail.vue'
import { useReadingToc } from '../../composables/ui/useReadingToc.js'

/**
 * Modale d'aide d'une page : un en-tête qui annonce à quoi on a affaire, le
 * contenu (`InfoContent` + ses `.info-section`) et, dès que l'aide est longue,
 * un sommaire latéral qui sert à la fois de plan et de navigation.
 *
 * Le sommaire est déduit du DOM du contenu à l'ouverture : le bandeau
 * `.info-lead` ouvre la liste, puis chaque `.info-section` y entre sous le
 * texte de son `h3`. Aucun des ~50 composants `*Info.vue` n'a à le déclarer.
 *
 * La mécanique de lecture et de suivi vit dans `useReadingToc`, et le rail
 * dans `TocRail` : la page « Documentation de l'API » réclamait le même plan
 * qui suit la lecture, et deux copies de cette règle n'auraient eu aucune
 * raison de rester d'accord.
 */
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  size: {
    type: String,
    default: 'large',
    validator: (value) => ['small', 'medium', 'large', 'xlarge'].includes(value)
  },
  /** Surtitre de l'en-tête : dit ce qu'est la fenêtre avant de dire son sujet. */
  kicker: {
    type: String,
    default: 'Aide de la page'
  },
  icon: {
    type: String,
    default: 'fas fa-circle-question'
  },
  closeButtonText: {
    type: String,
    default: 'J\'ai compris'
  }
})

defineEmits(['update:modelValue', 'close'])

/** En deçà, le contenu tient à l'écran : un sommaire n'aiderait personne.
 *  Le bandeau « L'essentiel » compte comme une entrée. */
const TOC_MIN_SECTIONS = 5

const scrollerRef = ref(null)
const contentRef = ref(null)

// `.info-lead` ouvre la liste (c'est l'ouverture de l'aide, pas une section),
// puis chaque `.info-section` y entre sous le texte de son `h3`.
const { sections, activeId, readSections, goTo, syncActive } = useReadingToc({
  root: contentRef,
  scroller: scrollerRef,
  selector: '.info-lead, .info-section',
  labelOf: (el) =>
    el.classList.contains('info-lead') ? 'L\'essentiel' : el.querySelector('h3')?.textContent?.trim(),
  idPrefix: 'info-section'
})

const showToc = computed(() => sections.value.length >= TOC_MIN_SECTIONS)

// Une aide à sommaire a besoin de deux colonnes : la largeur demandée par la
// page ne vaut que pour les aides courtes.
const effectiveSize = computed(() => (showToc.value ? 'xlarge' : props.size))

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) {
      sections.value = []
      return
    }
    // `nextTick` suffit : le contenu de la modale est monté et mesuré avant le
    // premier rendu à l'écran, la largeur ne saute donc pas à l'ouverture.
    await nextTick()
    readSections()
    if (scrollerRef.value) scrollerRef.value.scrollTop = 0
  },
  { immediate: true }
)
</script>

<style scoped>
/* ═══ En-tête ═══ */
.info-head {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  min-width: 0;
}

.info-head-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-lg);
  background: var(--color-primary-lightest);
  color: var(--color-primary);
  font-size: 1rem;
  flex-shrink: 0;
}

.info-head-titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.info-head-kicker {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

/* Le h2 global de modal-header impose 1.5rem : ici le surtitre porte déjà le
   rôle de la fenêtre, le titre n'a pas besoin de crier. */
.info-head-title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  line-height: 1.3;
}

/* ═══ Corps ═══ */
/* `minmax(0, 1fr)` en ligne : sans plancher à 0, la piste se dimensionne sur
   son contenu et déborde au lieu de laisser les colonnes défiler. */
.info-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  flex: 1;
  min-height: 0;
  min-width: 0;
}

.info-layout.has-toc {
  grid-template-columns: 248px minmax(0, 1fr);
}

/* Le volet qui porte le rail : son fond et son filet appartiennent à la
   modale, l'apparence des entrées appartient à TocRail. */
.info-toc {
  background: var(--color-bg-secondary);
  border-right: 1px solid var(--color-border-light);
  padding: var(--spacing-4) var(--spacing-3);
  overflow-y: auto;
}

/* `position: relative` obligatoire : c'est ce qui fait de la zone défilante
   l'ancêtre de référence des `offsetTop` lus pour le suivi de lecture. */
.info-scroller {
  position: relative;
  overflow-y: auto;
  min-height: 0;
  padding: var(--spacing-6) var(--spacing-8);
  min-width: 0;
}

.info-slot {
  max-width: 700px;
}

.info-tail {
  height: 420px;
}

@media (max-width: 768px) {
  /* Le sommaire coûterait un écran entier avant la première phrase. */
  .info-layout.has-toc {
    grid-template-columns: minmax(0, 1fr);
  }

  .info-toc {
    display: none;
  }

  .info-scroller {
    padding: var(--spacing-5) var(--spacing-4);
    -webkit-overflow-scrolling: touch;
  }

  .info-tail {
    height: 0;
  }
}
</style>
